import { useQuery } from '@tanstack/react-query'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { analyticsApi } from 'src/services/analyticsApi'

// 'not_monitored' is only emitted by /ads-library/page-summary — monitoring
// pages are tracked by definition. Kept in this union for shared consumption.
export type SnapshotStatus = 'failed' | 'not_monitored' | 'pending' | 'ready'

export interface PageSnapshot {
  active_count: number
  generated_at: string
  inactive_count: number
  languages: Record<string, number>
  platforms: Record<string, number>
  target_countries: { code?: string; count: number; name: string }[]
  top_creatives: { count: number; text: string }[]
  total_count: number
}

export interface MonitoringPage {
  _id: string
  ad_reached_countries?: string[]
  ad_type?: string
  category?: string | null
  created_at?: string
  last_snapshot_at?: string | null
  latest_snapshot?: PageSnapshot | null
  page_id?: string
  page_name?: string
  page_picture?: string | null
  snapshot_status?: SnapshotStatus | null
  status?: string
  updated_at?: string
  verification_status?: string | null
}

interface MonitoringPagesResponse {
  data?: MonitoringPage[]
  success?: boolean
}

const getMonitoringPages =
  async (): Promise<MonitoringPagesResponse | null> => {
    const sessionId = getSessionIdFromLocalStorage()
    if (!sessionId) return null

    const { data } = await analyticsApi().get<MonitoringPagesResponse>(
      '/v1_private/ads-library/monitoring-pages'
    )

    return data
  }

export const useGetMonitoringPagesQuery = () => {
  const sessionId = getSessionIdFromLocalStorage()

  return useQuery({
    enabled: !!sessionId,
    queryFn: getMonitoringPages,
    queryKey: [QueryKey.GET_MONITORING_PAGES],
    // Poll while any saved page is still being snapshotted by the worker, so
    // cards flip from "Syncing…" to real numbers without a manual refresh.
    refetchInterval: (query) => {
      const pages = query.state.data?.data || []
      return pages.some((p) => p.snapshot_status === 'pending') ? 10_000 : false
    },
  })
}
